import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@/api/client';
import GoogleCalendarSection from './GoogleCalendarSection';
import type { InterviewSlot, SlotGenerateResult, Vacancy } from '@/types';

const WEEKDAYS = [
  { value: 1, short: 'Пн' },
  { value: 2, short: 'Вт' },
  { value: 3, short: 'Ср' },
  { value: 4, short: 'Чт' },
  { value: 5, short: 'Пт' },
  { value: 6, short: 'Сб' },
  { value: 0, short: 'Вс' },
];

// В API дни недели: 0=понедельник … 6=воскресенье (как в Python weekday()).
const toPyWeekday = (jsDay: number) => (jsDay + 6) % 7;

const isoDate = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const addDays = (d: Date, days: number) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const dayTitle = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

export default function Interviews() {
  const today = useMemo(() => new Date(), []);

  const [slots, setSlots] = useState<InterviewSlot[]>([]);
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const [filterVacancy, setFilterVacancy] = useState('');
  const [showPast, setShowPast] = useState(false);

  // форма генератора
  const [dateFrom, setDateFrom] = useState(isoDate(today));
  const [dateTo, setDateTo] = useState(isoDate(addDays(today, 13)));
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [timeFrom, setTimeFrom] = useState('10:00');
  const [timeTo, setTimeTo] = useState('18:00');
  const [duration, setDuration] = useState(45);
  const [breakMin, setBreakMin] = useState(15);
  const [location, setLocation] = useState('');
  const [genVacancy, setGenVacancy] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([
        api.get<InterviewSlot[]>(`/interview-slots${filterVacancy ? `?vacancy_id=${filterVacancy}` : ''}`),
        api.get<Vacancy[]>('/vacancies').catch(() => []),
      ]);
      setSlots(s || []);
      setVacancies(v || []);
      setError('');
    } catch (e) {
      setError((e as Error).message || 'Не удалось загрузить окна');
    } finally {
      setLoading(false);
    }
  }, [filterVacancy]);

  useEffect(() => { load(); }, [load]);

  const toggleWeekday = (jsDay: number) => {
    setWeekdays((prev) =>
      prev.includes(jsDay) ? prev.filter((d) => d !== jsDay) : [...prev, jsDay],
    );
  };

  const handleGenerate = async () => {
    if (!weekdays.length) { setNotice('Выберите хотя бы один день недели'); return; }
    setBusy(true);
    setNotice('');
    try {
      const res = await api.post<SlotGenerateResult>('/interview-slots/generate', {
        date_from: dateFrom,
        date_to: dateTo,
        weekdays: weekdays.map(toPyWeekday),
        time_from: timeFrom,
        time_to: timeTo,
        duration_min: duration,
        break_min: breakMin,
        tz_offset_min: -new Date().getTimezoneOffset(),
        vacancy_id: genVacancy || null,
        location: location.trim() || null,
      });
      setNotice(
        res.created > 0
          ? `Создано окон: ${res.created}${res.skipped ? `, пропущено (уже были или в прошлом): ${res.skipped}` : ''}`
          : 'Новых окон не создано — все эти слоты уже существуют',
      );
      await load();
    } catch (e) {
      setNotice((e as Error).message || 'Ошибка генерации');
    }
    setBusy(false);
  };

  const handleDelete = async (slot: InterviewSlot) => {
    if (slot.status === 'booked') {
      if (!confirm(`Окно занято: ${slot.candidate_name || 'кандидат'}. Отменить бронь и написать кандидату?`)) return;
      setBusy(true);
      try {
        await api.post(`/interview-slots/${slot.id}/cancel`, {});
        setNotice('Бронь отменена, кандидату отправлено письмо. Окно снова свободно.');
        await load();
      } catch (e) {
        setNotice((e as Error).message || 'Ошибка отмены');
      }
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      await api.del(`/interview-slots/${slot.id}`);
      setSlots((prev) => prev.filter((s) => s.id !== slot.id));
    } catch (e) {
      setNotice((e as Error).message || 'Ошибка удаления');
    }
    setBusy(false);
  };

  const now = Date.now();
  const visible = showPast ? slots : slots.filter((s) => new Date(s.starts_at).getTime() > now);

  const byDay = useMemo(() => {
    const map = new Map<string, InterviewSlot[]>();
    for (const slot of visible) {
      const key = isoDate(new Date(slot.starts_at));
      const list = map.get(key) || [];
      list.push(slot);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  const freeCount = visible.filter((s) => s.status === 'free').length;
  const bookedCount = visible.filter((s) => s.status === 'booked').length;
  const tzLabel = useMemo(() => {
    const offset = -new Date().getTimezoneOffset() / 60;
    return `UTC${offset >= 0 ? '+' : '−'}${Math.abs(offset)}`;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950">Интервью</h1>
        <p className="text-sm text-foreground-500 mt-1">
          Вы проставляете свободные окна — кандидат по персональной ссылке выбирает удобное.
          Время показано в вашем поясе ({tzLabel}).
        </p>
      </div>

      {/* Генератор окон */}
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <h2 className="text-base font-heading font-semibold text-foreground-900 mb-4">
          Проставить окна
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">С даты</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">По дату</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Время с</span>
            <input
              type="time"
              value={timeFrom}
              onChange={(e) => setTimeFrom(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Время до</span>
            <input
              type="time"
              value={timeTo}
              onChange={(e) => setTimeTo(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Длительность, мин</span>
            <input
              type="number"
              min={5}
              max={480}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Перерыв между, мин</span>
            <input
              type="number"
              min={0}
              max={240}
              value={breakMin}
              onChange={(e) => setBreakMin(Number(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Вакансия</span>
            <select
              value={genVacancy}
              onChange={(e) => setGenVacancy(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Любая (общие окна)</option>
              {vacancies.map((v) => (
                <option key={v.id} value={v.id}>{v.title}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Место / ссылка на встречу</span>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Google Meet, Zoom, офис…"
              className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </label>
        </div>

        <div className="mt-4">
          <span className="text-xs font-medium text-foreground-500 mb-2 block">Дни недели</span>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => {
              const active = weekdays.includes(d.value);
              return (
                <button
                  key={d.value}
                  onClick={() => toggleWeekday(d.value)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    active
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <button
            onClick={handleGenerate}
            disabled={busy}
            className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-2"
          >
            <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-calendar-check-line'}></i>
            Создать окна
          </button>
          {notice && <span className="text-xs text-foreground-600">{notice}</span>}
        </div>
      </div>

      <GoogleCalendarSection vacancies={vacancies} />

      {/* Список окон */}
      <div className="bg-white rounded-xl border border-background-200">
        <div className="flex items-center justify-between gap-4 flex-wrap p-5 border-b border-background-200">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-heading font-semibold text-foreground-900">Окна</h2>
            <span className="text-xs text-foreground-500">
              <span className="font-bold text-emerald-600">{freeCount}</span> свободных ·{' '}
              <span className="font-bold text-amber-600">{bookedCount}</span> занятых
            </span>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={filterVacancy}
              onChange={(e) => setFilterVacancy(e.target.value)}
              className="text-xs border border-background-200 rounded-lg px-2.5 py-2 bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Все вакансии</option>
              {vacancies.map((v) => (
                <option key={v.id} value={v.id}>{v.title}</option>
              ))}
            </select>
            <label className="text-xs text-foreground-500 flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
                className="cursor-pointer"
              />
              Показывать прошедшие
            </label>
          </div>
        </div>

        {error && <p className="p-5 text-sm text-red-600">{error}</p>}

        {!error && byDay.length === 0 && (
          <p className="p-8 text-sm text-foreground-400 text-center">
            Окон пока нет. Задайте рабочие часы выше и нажмите «Создать окна».
          </p>
        )}

        <div className="divide-y divide-background-200">
          {byDay.map(([day, daySlots]) => (
            <div key={day} className="p-5">
              <p className="text-sm font-medium text-foreground-800 mb-3 capitalize">
                {dayTitle(day)}
                <span className="text-xs text-foreground-400 font-normal ml-2">
                  {daySlots.length} окон
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {daySlots
                  .slice()
                  .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
                  .map((slot) => {
                    const booked = slot.status === 'booked';
                    return (
                      <div
                        key={slot.id}
                        className={`group flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg border text-sm ${
                          booked
                            ? 'bg-amber-50 border-amber-300 text-amber-900'
                            : 'bg-background-50 border-background-200 text-foreground-700'
                        }`}
                      >
                        <span className="font-medium tabular-nums">{timeLabel(slot.starts_at)}</span>
                        <span className="text-xs text-foreground-400">{slot.duration_min}м</span>
                        {booked && (
                          <span className="text-xs font-medium max-w-[180px] truncate">
                            <i className="ri-user-line mr-1"></i>
                            {slot.candidate_name || 'кандидат'}
                          </span>
                        )}
                        {slot.vacancy_title && (
                          <span className="text-[11px] text-foreground-400 max-w-[120px] truncate">
                            {slot.vacancy_title}
                          </span>
                        )}
                        <button
                          onClick={() => handleDelete(slot)}
                          disabled={busy}
                          title={booked ? 'Отменить бронь' : 'Удалить окно'}
                          className="w-5 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
